import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, Search, X, Mail, Phone, Trash2, Linkedin, FilterX, Lock,
} from "lucide-react";
import { toast } from "sonner";
import MultiSelectPopover from "@/components/MultiSelectPopover";
import { revealContact, fetchListMembers } from "@/lib/searchClient";
import { useBillingDialogs } from "@/contexts/BillingDialogs";

const SENIORITY_OPTIONS = [
  "Entry", "Senior", "Manager", "Director", "VP", "C-Suite", "Owner/Founder",
].map((s) => ({ label: s, value: s }));

const EMAIL_STATUS_OPTIONS = [
  { label: "Verified", value: "verified" },
  { label: "Unverified", value: "unverified" },
  { label: "Catch-all", value: "catch-all" },
];

const defaultFilters = {
  q: "",
  title: "",
  seniority: [],
  email_status: [],
  industry: "",
  location: "",
  has_phone: false,
  has_email: false,
  revealed_only: false,
};

export default function ListDetail() {
  const { id } = useParams();
  const { user, refreshUser } = useAuth();
  const billing = useBillingDialogs();
  const navigate = useNavigate();

  const [listName, setListName] = useState("");
  const [members, setMembers] = useState(null); // null = loading
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [revealing, setRevealing] = useState(null); // `${personId}:${type}`
  const [f, setF] = useState(defaultFilters);

  const memberKey = (m) => `${m.entity_type}-${m.person_id || m.company_id}`;

  const fetchAll = async () => {
    if (!user?.id) return;
    const listRes = await supabase
      .from("user_lists").select("name").eq("id", id).eq("user_id", user.id).single();
    if (listRes.error) { toast.error("List not found"); navigate("/lists"); return; }
    setListName(listRes.data.name);
    try {
      // Routed by searchClient: list_members() RPC, or Supabase memberships
      // hydrated from the catalog when it is enabled.
      setMembers(await fetchListMembers(id));
    } catch (e) {
      toast.error(e.message || "Could not load list members");
      setMembers([]);
    }
  };

  useEffect(() => { fetchAll(); }, [id, user?.id]); // eslint-disable-line

  // ── Client-side filtering (lists are small; mirrors Research filters) ──
  const filtered = useMemo(() => {
    const all = members || [];
    const q = f.q.trim().toLowerCase();
    const title = f.title.trim().toLowerCase();
    const industry = f.industry.trim().toLowerCase();
    const location = f.location.trim().toLowerCase();
    return all.filter((m) => {
      if (q && !(
        (m.name || "").toLowerCase().includes(q) ||
        (m.title || "").toLowerCase().includes(q) ||
        (m.domain || "").toLowerCase().includes(q) ||
        (m.email || "").toLowerCase().includes(q)
      )) return false;
      if (title && !(m.title || "").toLowerCase().includes(title)) return false;
      if (f.seniority.length && !f.seniority.includes(m.seniority)) return false;
      if (f.email_status.length && !f.email_status.includes(m.email_status)) return false;
      if (industry && !(m.industry || "").toLowerCase().includes(industry)) return false;
      if (location && !(m.location || "").toLowerCase().includes(location)) return false;
      if (f.has_phone && !m.has_phone) return false;
      if (f.has_email && !m.has_email) return false;
      if (f.revealed_only && !(m.email_revealed || m.phone_revealed)) return false;
      return true;
    });
  }, [members, f]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (f.q.trim()) n++;
    if (f.title.trim()) n++;
    if (f.seniority.length) n++;
    if (f.email_status.length) n++;
    if (f.industry.trim()) n++;
    if (f.location.trim()) n++;
    if (f.has_phone) n++;
    if (f.has_email) n++;
    if (f.revealed_only) n++;
    return n;
  }, [f]);

  const clearFilters = () => setF(defaultFilters);

  // ── Selection ──
  const allSelected = filtered.length > 0 && filtered.every((m) => selectedIds.has(memberKey(m)));
  const toggleAll = () => setSelectedIds((prev) => {
    const n = new Set(prev);
    if (allSelected) filtered.forEach((m) => n.delete(memberKey(m)));
    else filtered.forEach((m) => n.add(memberKey(m)));
    return n;
  });
  const toggleOne = (m) => setSelectedIds((prev) => {
    const n = new Set(prev);
    const k = memberKey(m);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });
  const selectedCount = (members || []).filter((m) => selectedIds.has(memberKey(m))).length;

  // ── Reveal (person rows) ──
  const doReveal = async (e, m, type) => {
    e.stopPropagation();
    if (m.entity_type !== "person") return;
    setRevealing(`${m.person_id}:${type}`);
    // Routes to the Postgres RPC or, for catalog people, the reveal-contact
    // Edge Function. Both are charge-on-success.
    const res = await revealContact(m.person_id, type);
    setRevealing(null);

    if (!res.ok) {
      // Out of credits, today's reveal limit and a paused account each open
      // their own pop-up (BillingDialogs); misses and outages stay toasts.
      if (billing.handleRevealError(res)) {
        // handled
      } else if (res.error === "no_value") {
        toast.error(type === "email" ? "No email on file for this lead." : "No phone number on file for this lead.");
      } else if (res.error === "lookup_failed") {
        toast.error("Contact lookup is temporarily unavailable — you were not charged.");
      } else {
        toast.error("Could not reveal contact.");
      }
      return;
    }

    setMembers((prev) => (prev || []).map((x) => memberKey(x) === memberKey(m) ? { ...x, ...res.patch } : x));
    if (!res.alreadyRevealed) refreshUser();
  };

  // ── Remove ──
  const removeMembers = async (toRemove) => {
    const personIds = toRemove.filter((m) => m.entity_type === "person").map((m) => m.person_id);
    const companyIds = toRemove.filter((m) => m.entity_type === "company").map((m) => m.company_id);
    if (personIds.length) {
      const { error } = await supabase.from("list_contacts").delete().eq("list_id", id).in("person_id", personIds);
      if (error) { toast.error(error.message); return false; }
    }
    if (companyIds.length) {
      const { error } = await supabase.from("list_contacts").delete().eq("list_id", id).in("company_id", companyIds);
      if (error) { toast.error(error.message); return false; }
    }
    const removedKeys = new Set(toRemove.map(memberKey));
    setMembers((prev) => (prev || []).filter((m) => !removedKeys.has(memberKey(m))));
    setSelectedIds((prev) => { const n = new Set(prev); removedKeys.forEach((k) => n.delete(k)); return n; });
    return true;
  };

  const removeOne = async (m) => {
    if (await removeMembers([m])) toast.success("Removed from list");
  };
  const bulkRemove = async () => {
    const selected = (members || []).filter((m) => selectedIds.has(memberKey(m)));
    if (selected.length === 0) return;
    if (await removeMembers(selected)) toast.success(`Removed ${selected.length} lead(s)`);
  };

  const set = (patch) => setF((prev) => ({ ...prev, ...patch }));

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-4 md:px-10">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/lists")} aria-label="Back to lists">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="eyebrow">Lists</div>
            <h1 className="font-display text-2xl tracking-tight">{listName || "…"}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {members === null ? "…" : `${filtered.length} of ${members.length}`} leads
          </span>
        </div>
      </header>

      {/* ── Filter bar ── */}
      <div className="border-b border-border bg-surface/50 px-6 py-3 md:px-10">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={f.q}
                onChange={(e) => set({ q: e.target.value })}
                placeholder="Search name, title, company, email…"
                className="h-9 pl-8"
                data-testid="list-search"
              />
            </div>
          </div>
          <div className="w-40">
            <Input value={f.title} onChange={(e) => set({ title: e.target.value })} placeholder="Title contains" className="h-9" data-testid="list-title" />
          </div>
          <div className="w-44">
            <MultiSelectPopover
              options={SENIORITY_OPTIONS}
              values={f.seniority}
              onChange={(v) => set({ seniority: v })}
              placeholder="Seniority"
              testid="list-seniority"
            />
          </div>
          <div className="w-44">
            <MultiSelectPopover
              options={EMAIL_STATUS_OPTIONS}
              values={f.email_status}
              onChange={(v) => set({ email_status: v })}
              placeholder="Email status"
              testid="list-email-status"
            />
          </div>
          <div className="w-36">
            <Input value={f.industry} onChange={(e) => set({ industry: e.target.value })} placeholder="Industry" className="h-9" data-testid="list-industry" />
          </div>
          <div className="w-36">
            <Input value={f.location} onChange={(e) => set({ location: e.target.value })} placeholder="Location" className="h-9" data-testid="list-location" />
          </div>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Switch checked={f.has_email} onCheckedChange={(v) => set({ has_email: v })} /> Has email
          </label>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Switch checked={f.has_phone} onCheckedChange={(v) => set({ has_phone: v })} /> Has phone
          </label>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Switch checked={f.revealed_only} onCheckedChange={(v) => set({ revealed_only: v })} /> Revealed only
          </label>
          {activeFilterCount > 0 && (
            <Button variant="ghost" size="sm" className="h-9 gap-1.5 text-muted-foreground" onClick={clearFilters} data-testid="list-clear-filters">
              <FilterX className="h-3.5 w-3.5" /> Clear ({activeFilterCount})
            </Button>
          )}
        </div>
      </div>

      {/* ── Bulk actions ── */}
      {selectedCount > 0 && (
        <div className="flex items-center gap-2 border-b border-border bg-violet-soft px-6 py-2 md:px-10">
          <span className="text-sm">{selectedCount} selected</span>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="outline" className="h-8 gap-1.5 text-destructive">
                <Trash2 className="h-3.5 w-3.5" /> Remove selected
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove {selectedCount} lead(s) from "{listName}"?</AlertDialogTitle>
                <AlertDialogDescription>
                  This only removes them from this list. The leads themselves are not deleted.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={bulkRemove}>Remove</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {/* ── Table ── */}
      <div className="flex-1 overflow-auto bg-surface">
        {members === null ? (
          <div className="space-y-2 p-6">
            {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : members.length === 0 ? (
          <div className="grid h-full place-items-center p-12 text-center">
            <div>
              <h3 className="font-display text-xl tracking-tight">This list is empty</h3>
              <p className="mt-1 text-sm text-muted-foreground">Add leads from the Research page.</p>
              <Button className="mt-4" onClick={() => navigate("/research")}>Go to Research</Button>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="grid h-full place-items-center p-12 text-center text-sm text-muted-foreground">
            No leads match these filters.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-surface-inset">
              <tr className="border-b border-border bg-surface-inset text-left text-xs font-medium text-muted-foreground">
                <th className="w-10 px-6 py-2.5 md:px-10">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all" />
                </th>
                <th className="px-3 py-2.5 font-medium">Name</th>
                <th className="px-3 py-2.5 font-medium">Title</th>
                <th className="px-3 py-2.5 font-medium">Email</th>
                <th className="px-3 py-2.5 font-medium">Mobile / Direct</th>
                <th className="px-3 py-2.5 font-medium">Company number</th>
                <th className="px-3 py-2.5 font-medium">Industry</th>
                <th className="px-3 py-2.5 font-medium">Location</th>
                <th className="w-10 px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => {
                const isPerson = m.entity_type === "person";
                const key = memberKey(m);
                // Personal lines only — the shared corporate switchboard is
                // shown separately in its own (free) column.
                const phone = m.mobile_phone || m.work_direct_phone || m.work_direct_phone_2 || m.home_phone;
                return (
                  <tr key={key} className={`border-b border-border/60 ${selectedIds.has(key) ? "bg-violet-soft" : "hover:bg-surface-hover"}`}>
                    <td className="px-6 py-2.5 md:px-10">
                      <Checkbox checked={selectedIds.has(key)} onCheckedChange={() => toggleOne(m)} aria-label={`Select ${m.name}`} />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-accent text-[11px] text-accent-foreground">
                          {(m.name || "?")[0]?.toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate font-medium">{m.name || "—"}</div>
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <span className="truncate">{m.domain || "—"}</span>
                            {m.linkedin_url && (
                              <a href={m.linkedin_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-violet-text/70 hover:text-violet-text">
                                <Linkedin className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="truncate">{m.title || "—"}</div>
                      {m.seniority && <div className="text-xs text-muted-foreground">{m.seniority}</div>}
                    </td>
                    <td className="px-3 py-2.5">
                      {!isPerson ? (
                        <span className="text-muted-foreground">—</span>
                      ) : m.email_revealed ? (
                        <div className="flex items-center gap-1 text-xs">
                          <Mail className="h-3 w-3 text-muted-foreground" />
                          <span className="truncate">{m.email || "—"}</span>
                        </div>
                      ) : m.has_email ? (
                        <button
                          onClick={(e) => doReveal(e, m, "email")}
                          disabled={revealing === `${m.person_id}:email`}
                          className="flex items-center gap-1 text-xs text-violet-text hover:underline disabled:opacity-50"
                          data-testid={`reveal-email-${m.person_id}`}
                        >
                          <Lock className="h-3 w-3" />
                          {revealing === `${m.person_id}:email` ? "Revealing…" : "Reveal"}
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground">No email</span>
                      )}
                    </td>
                    {/* Mobile / direct — personal, credit-gated. */}
                    <td className="px-3 py-2.5">
                      {!isPerson ? (
                        <span className="text-muted-foreground">—</span>
                      ) : m.phone_revealed ? (
                        <div className="flex items-center gap-1 text-xs">
                          <Phone className="h-3 w-3 text-muted-foreground" />
                          <span className="truncate">{phone || "—"}</span>
                        </div>
                      ) : m.has_phone ? (
                        <button
                          onClick={(e) => doReveal(e, m, "phone")}
                          disabled={revealing === `${m.person_id}:phone`}
                          className="flex items-center gap-1 text-xs text-violet-text hover:underline disabled:opacity-50"
                          data-testid={`reveal-phone-${m.person_id}`}
                        >
                          <Lock className="h-3 w-3" />
                          {revealing === `${m.person_id}:phone` ? "Revealing…" : "Reveal"}
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground">No mobile</span>
                      )}
                    </td>
                    {/* Company switchboard — its own 1-credit reveal (029). */}
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">
                      {m.company_phone
                        ? m.company_phone
                        : m.has_company_phone
                          ? (
                            <button
                              onClick={(e) => doReveal(e, m, "company_phone")}
                              disabled={revealing === `${m.person_id}:company_phone`}
                              className="flex items-center gap-1 text-xs text-violet-text hover:underline disabled:opacity-50"
                            >
                              <Lock className="h-3 w-3" />
                              {revealing === `${m.person_id}:company_phone` ? "Revealing…" : "Reveal"}
                            </button>
                          )
                          : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">{m.industry || "—"}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{m.location || "—"}</td>
                    <td className="px-3 py-2.5">
                      <Button variant="ghost" size="icon" className="h-7 w-7" aria-label={`Remove ${m.name}`} onClick={() => removeOne(m)}>
                        <X className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
